"use client"

import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { InfoIcon, DollarSignIcon, PercentIcon } from "lucide-react"

// Import new modular components and utilities
import { SalaryLocationSection } from "./calculator/salary-location-section"
import { WorkScheduleSection } from "./calculator/work-schedule-section"
import { PaidTimeOffSection } from "./calculator/paid-time-off-section"
import { CostBreakdownTable } from "./calculator/cost-breakdown-table"
import { useCalculatorData } from "@/hooks/use-calculator-data"
import { useWcbRate } from "@/hooks/use-wcb-rate"
import { useTaxData } from "@/hooks/use-tax-data"
import { calculateTotalEmployerCost, calculateBurdenRate } from "@/lib/utils/cost-calculations"
import { getDefaultOntarioProvince } from "@/lib/utils/province-utils"
import { useLabourBurdenApi } from "@/lib/api"
import { sendMessageToParent } from "@/lib/app-messaging"
import { CPP_QPP_EI_RATES, WSIB_WCB_SAMPLE } from "@/lib/constants/province-data"
import { 
  CalculatorProps, 
  CalculatorFormData, 
  CalculatorInitialData,
  CalculatorProvince,
  WorkerCompensationItemWithDisplay,
  CostCalculationResult
} from "@/lib/types/calculator"

export default function LabourBurdenCalculator({
  initialData,
  onSave,
  bearerToken,
}: CalculatorProps) {
  // Ensure we have a valid initial data object
  const safeInitialData: CalculatorInitialData = initialData || {}
  
  // API calculation state
  const [apiCosts, setApiCosts] = useState<CostCalculationResult | null>(null)
  const [apiCalculationLoading, setApiCalculationLoading] = useState(false)
  const [apiCalculationError, setApiCalculationError] = useState<string | null>(null)
  const useApiCalculation = true // Always use API for consistency with FBO application
  
  // Form state management
  const [form, setForm] = useState<CalculatorFormData>({
    salary: safeInitialData.salary?.toString() || "",
    province: safeInitialData.province || "",
    wsibCode: safeInitialData.wsibCode || "",
    wsibRateOverride: "",
    wsibActualRate: "0",
    vacationDays: safeInitialData.vacationDays?.toString() || "0",
    sickDays: safeInitialData.sickDays?.toString() || "0",
    numPublicHolidays: safeInitialData.numPublicHolidays?.toString() || "0",
    otherPaidLeave: "0",
    benefits: "0",
    bonusesIncentives: safeInitialData.bonusAmount?.toString() || "0",
    bonusesIncentivesPercent: safeInitialData.bonusPercent?.toString() || "0",
    includeEHT: true,
    trainingOverhead: "0",
    applyCpp: safeInitialData.applyCpp ?? true,
    applyQpp: safeInitialData.applyQpp ?? true,
    applyEi: safeInitialData.applyEi ?? true,
    hoursPerDay: safeInitialData.hoursPerDay?.toString() || "8",
  })

  // Load provinces and WCB data using custom hooks
  const {
    provinces,
    provincesLoading,
    provincesError,
    wcbData,
    wcbLoading,
    wcbError,
    getCurrentSubdivisionId
  } = useCalculatorData({
    provinceId: form.province,
    subdivisionId: safeInitialData.subdivisionId
  })

  // Load WCB rate for the selected code and organization
  const { wcbRate, wcbRateLoading } = useWcbRate({
    wcbCode: form.wsibCode,
    subdivisionId: getCurrentSubdivisionId(),
    organizationId: safeInitialData.organizationId
  })

  // Load tax data for CPP/CPP2/EI calculations
  const { taxData, taxDataLoading, taxDataError } = useTaxData({
    subdivisionId: getCurrentSubdivisionId(),
    enabled: true
  })

  // API calculation hook
  const labourBurdenApi = useLabourBurdenApi()

  // Update form state when WCB rate is loaded
  useEffect(() => {
    setForm(prev => ({ ...prev, wsibActualRate: wcbRate.toString() }))
  }, [wcbRate])

  // Update form when initialData changes (from Angular app)
  useEffect(() => {
    if (initialData) {
      setForm(prevForm => ({
        ...prevForm,
        salary: initialData.salary?.toString() || prevForm.salary,
        province: initialData.province || prevForm.province,
        wsibCode: initialData.wsibCode || prevForm.wsibCode,
        vacationDays: initialData.vacationDays?.toString() || prevForm.vacationDays,
        sickDays: initialData.sickDays?.toString() || prevForm.sickDays,
        numPublicHolidays: initialData.numPublicHolidays?.toString() || prevForm.numPublicHolidays,
        applyCpp: initialData.applyCpp ?? prevForm.applyCpp,
        applyQpp: initialData.applyQpp ?? prevForm.applyQpp,
        applyEi: initialData.applyEi ?? prevForm.applyEi,
        hoursPerDay: initialData.hoursPerDay?.toString() || prevForm.hoursPerDay,
        bonusesIncentives: initialData.bonusAmount?.toString() || prevForm.bonusesIncentives,
        bonusesIncentivesPercent: initialData.bonusPercent?.toString() || prevForm.bonusesIncentivesPercent,
        // Preserve wsibActualRate - don't reset it when initialData changes
        wsibActualRate: prevForm.wsibActualRate,
      }))
    }
  }, [initialData])

  // Set default public holidays based on province
  useEffect(() => {
    if (provinces.length === 0) return
    
    const province = provinces.find((p) => p.id === form.province)
    const hasInitialHolidaysValue = safeInitialData.numPublicHolidays !== undefined
    
    if (province && !hasInitialHolidaysValue && !form.numPublicHolidays) {
      setForm((prev) => ({
        ...prev,
        numPublicHolidays: province.defaultPublicHolidays.toString(),
      }))
    }
  }, [form.province, form.numPublicHolidays, safeInitialData.numPublicHolidays, provinces])

  // Clear WCB selection when switching provinces if code not available
  useEffect(() => {
    // Only check if WCB data is loaded (not loading) and we have a code to check
    if (form.wsibCode && !wcbLoading && wcbData.length > 0) {
      const foundCode = wcbData.find(item => item.Code === form.wsibCode);
      
      if (!foundCode) {
        console.warn(`WCB Code ${form.wsibCode} not found in loaded data, clearing selection`);
        setForm(prev => ({ ...prev, wsibCode: "", wsibActualRate: "0" }))
      }
    }
  }, [wcbData, form.wsibCode, wcbLoading])

  // Memoize the key calculation parameters to prevent unnecessary re-renders
  const calculationParams = useMemo(() => ({
    salary: form.salary,
    province: form.province, 
    wsibCode: form.wsibCode,
    wsibRateOverride: form.wsibRateOverride,
    applyCpp: form.applyCpp,
    applyQpp: form.applyQpp,
    applyEi: form.applyEi,
    vacationDays: form.vacationDays,
    sickDays: form.sickDays,
    numPublicHolidays: form.numPublicHolidays,
    hoursPerDay: form.hoursPerDay,
    includeEHT: form.includeEHT,
    benefits: form.benefits,
    bonusesIncentives: form.bonusesIncentives,
    trainingOverhead: form.trainingOverhead
  }), [
    form.salary,
    form.province, 
    form.wsibCode,
    form.wsibRateOverride,
    form.applyCpp,
    form.applyQpp,
    form.applyEi,
    form.vacationDays,
    form.sickDays,
    form.numPublicHolidays,
    form.hoursPerDay,
    form.includeEHT,
    form.benefits,
    form.bonusesIncentives,
    form.trainingOverhead
  ]);

  // Use ref to track last calculation params to prevent duplicate calls
  const lastCalculationParams = useRef<string>('');

  // Memoized API calculation function
  const performApiCalculation = useCallback(async () => {
    if (!useApiCalculation) return;

    // Only calculate if we have minimum required data
    if (!calculationParams.salary || !calculationParams.province || parseFloat(calculationParams.salary) <= 0) {
      setApiCosts(null);
      return;
    }

    // Check if parameters actually changed
    const currentParamsString = JSON.stringify(calculationParams);
    if (lastCalculationParams.current === currentParamsString) {
      console.log('Skipping API call - parameters unchanged');
      return;
    }

    lastCalculationParams.current = currentParamsString;
    console.log('Making API call with new parameters:', calculationParams);

    setApiCalculationLoading(true);
    setApiCalculationError(null);

    try {
      // Create selected province inline to avoid dependency issues
      const selectedProvince = provinces.length === 0 
        ? getDefaultOntarioProvince()
        : provinces.find((p) => p.id === calculationParams.province) || provinces[0];
        
      const result = await labourBurdenApi.calculateLabourBurden(form, selectedProvince, safeInitialData.organizationId);

      if (result.success && result.data) {
        setApiCosts(result.data);
        console.log('API calculation successful:', result.data);
      } else {
        setApiCalculationError(result.error || 'API calculation failed');
        console.error('API calculation failed:', result.error);
      }
    } catch (error) {
      setApiCalculationError(error instanceof Error ? error.message : 'Unknown error');
      console.error('API calculation error:', error);
    } finally {
      setApiCalculationLoading(false);
    }
  }, [useApiCalculation, calculationParams, provinces, labourBurdenApi, form]);

  // API calculation effect - triggers when memoized params change
  useEffect(() => {
    if (!useApiCalculation) return;

    // Debounce API calls - wait 500ms after parameters change for good responsiveness
    const timeoutId = setTimeout(performApiCalculation, 500);
    return () => clearTimeout(timeoutId);
  }, [performApiCalculation, useApiCalculation])

  const handleFormChange = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const getSelectedProvince = (): CalculatorProvince => {
    if (provinces.length === 0) {
      return getDefaultOntarioProvince()
    }
    return provinces.find((p) => p.id === form.province) || provinces[0]
  }

  const getEffectiveWsibRate = (): number => {
    if (form.wsibRateOverride) {
      return Number.parseFloat(form.wsibRateOverride)
    }
    return Number.parseFloat(form.wsibActualRate) || 0
  }

  // Calculate results using API or utility functions
  const costs = useApiCalculation && apiCosts 
    ? apiCosts 
    : calculateTotalEmployerCost(form, getSelectedProvince(), getEffectiveWsibRate(), taxData)
  const burdenRate = calculateBurdenRate(costs.total, costs.basePay)

  const handleSave = () => {
    if (onSave) {
      const saveData = {
        formData: form,
        costs,
        burdenRate,
        timestamp: new Date().toISOString(),
      }

      onSave(saveData)

      // Send results back to Angular parent window if opened as popup
      if (window.opener && !window.opener.closed) {
        sendMessageToParent({
          type: 'CALCULATOR_RESULT',
          effectiveHourlyRate: costs.effectiveHourlyRate,
          totalCost: costs.total,
          burdenRate: burdenRate,
          formData: form,
          timestamp: new Date().toISOString(),
          // Include source identifier for routing (to distinguish modal vs profile calculator)
          source: safeInitialData.source
        })
      }
    }
  }

  // Formatting functions
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  const formatPercent = (value: number) => {
    return new Intl.NumberFormat("en-CA", {
      style: "percent",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100)
  }

  return (
    <div>
      <Tabs defaultValue="calculator" className="w-full">
        <TabsList className="grid grid-cols-2 mb-4">
          <TabsTrigger value="calculator">Calculator</TabsTrigger>
          <TabsTrigger value="reference">Reference Tables</TabsTrigger>
        </TabsList>

        <TabsContent value="calculator" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Inputs Column */}
            <div className="space-y-6">
              <SalaryLocationSection
                form={form}
                provinces={provinces}
                provincesLoading={provincesLoading}
                provincesError={provincesError}
                onFormChange={handleFormChange}
              />



              <WorkScheduleSection
                form={form}
                onFormChange={handleFormChange}
              />

              <PaidTimeOffSection
                form={form}
                selectedProvince={getSelectedProvince()}
                onFormChange={handleFormChange}
              />

              {/* Tax Deductions Section */}
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-4">Tax Deductions</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="applyCpp"
                          checked={form.applyCpp}
                          onCheckedChange={(checked) => handleFormChange("applyCpp", Boolean(checked))}
                        />
                        <Label htmlFor="applyCpp" className="flex items-center">
                          Apply CPP (Canada Pension Plan)
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Employer contribution for Canada Pension Plan. Applied outside Quebec.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="applyQpp"
                          checked={form.applyQpp}
                          onCheckedChange={(checked) => handleFormChange("applyQpp", Boolean(checked))}
                        />
                        <Label htmlFor="applyQpp" className="flex items-center">
                          Apply QPP (Quebec Pension Plan)
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Employer contribution for Quebec Pension Plan. Applied in Quebec only.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="applyEi"
                          checked={form.applyEi}
                          onCheckedChange={(checked) => handleFormChange("applyEi", Boolean(checked))}
                        />
                        <Label htmlFor="applyEi" className="flex items-center">
                          Apply EI (Employment Insurance)
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Employer contribution for Employment Insurance. Applied across Canada.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                      </div>
                    </div>
                    
                    {form.province === "QC" && (
                      <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-md">
                        <strong>Quebec Province:</strong> QPP is used instead of CPP. EI rates may differ.
                      </div>
                    )}
                    
                    {form.province !== "QC" && (
                      <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-md">
                        <strong>Outside Quebec:</strong> CPP is used for pension contributions.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Total Annual Employer Cost - moved from results column */}
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium">Total Annual Employer Cost</h3>
                  <div className="space-y-4">
                    {/* API and Tax Data Status */}
                    {(taxDataLoading || apiCalculationLoading) && (
                      <div className="text-sm text-blue-600">
                        🔄 {taxDataLoading ? 'Loading current tax rates...' : 'Calculating costs...'}
                      </div>
                    )}
                    {apiCalculationError && (
                      <div className="text-sm text-red-600">
                        ⚠️ API calculation failed - using fallback calculation
                      </div>
                    )}
                    {!taxDataLoading && !taxData && !apiCalculationError && (
                      <div className="text-sm text-amber-600">
                        {!bearerToken 
                          ? "⚠️ Using fallback tax rates - No authentication token available"
                          : taxDataError 
                            ? `⚠️ Using fallback tax rates - ${taxDataError}`
                            : "⚠️ Using fallback tax rates"
                        }
                      </div>
                    )}
                    {taxData && !apiCalculationError && (
                      <div className="text-sm text-green-600">
                        ✅ Using API calculations (Effective: {new Date(taxData.EffectiveDate).toLocaleDateString()})
                      </div>
                    )}
                    <div className="text-4xl font-bold text-center py-4">{formatCurrency(costs.total)}</div>
                    <div className="text-center">
                      <div className="text-sm text-muted-foreground">Base Pay: {formatCurrency(costs.basePay)}</div>
                      <div className="text-lg font-medium mt-2">
                        Labour Burden Rate: {formatPercent(burdenRate)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Results Column */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4">Additional Costs</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="wsibCode">WSIB/WCB Classification Code</Label>
                        {wcbLoading ? (
                          <div className="text-sm text-muted-foreground">Loading worker compensation codes...</div>
                        ) : wcbError ? (
                          <div className="text-sm text-red-600">{wcbError}</div>
                        ) : null}
                        <Select value={form.wsibCode} onValueChange={(value) => handleFormChange("wsibCode", value)}>
                          <SelectTrigger id="wsibCode">
                            <SelectValue placeholder="Select classification code" />
                          </SelectTrigger>
                          <SelectContent>
                            {wcbData.map((item) => (
                              <SelectItem key={`${item.Id}-${item.Code}`} value={item.Code}>
                                {item.DisplayText || `${item.Code} - ${item.Name}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {wcbRateLoading && (
                          <div className="text-sm text-muted-foreground">Loading rate...</div>
                        )}
                        {!safeInitialData.organizationId && (
                          <div className="text-sm text-amber-600">
                            ⚠️ Organization not specified - using manual rate override only
                          </div>
                        )}
                        {form.wsibActualRate && Number.parseFloat(form.wsibActualRate) > 0 && (
                          <div className="text-sm text-green-600">
                            Organization rate: {Number.parseFloat(form.wsibActualRate).toFixed(4)}%
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="wsibRateOverride" className="flex items-center">
                          WCB/WSIB Rate Override
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Override the organization-specific rate if needed. Leave blank to use the fetched rate.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                        <div className="relative">
                          <PercentIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="wsibRateOverride"
                            type="number"
                            value={form.wsibRateOverride}
                            onChange={(e) => handleFormChange("wsibRateOverride", e.target.value)}
                            className="pl-8"
                            placeholder={`Current: ${form.wsibActualRate}%`}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="benefits" className="flex items-center">
                          Benefits Cost ($/year)
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Include health benefits, dental, life insurance, RRSP match, etc.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                        <div className="relative">
                          <DollarSignIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="benefits"
                            type="number"
                            value={form.benefits}
                            onChange={(e) => handleFormChange("benefits", e.target.value)}
                            className="pl-8"
                            placeholder="e.g. 3600"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="bonusesIncentives" className="flex items-center">
                            Bonuses/Incentives ($/year)
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                <p>Include bonuses, incentives, etc. Syncs with percentage field.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </Label>
                          <div className="relative">
                            <DollarSignIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="bonusesIncentives"
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.bonusesIncentives}
                              onChange={(e) => {
                                const dollarValue = e.target.value
                                handleFormChange("bonusesIncentives", dollarValue)
                                // Auto-calculate percentage from dollar amount
                                const salary = Number.parseFloat(form.salary) || 0
                                const parsed = Number.parseFloat(dollarValue)
                                
                                if (Number.isFinite(parsed) && salary > 0) {
                                  const percent = (parsed / salary) * 100
                                  handleFormChange("bonusesIncentivesPercent", percent.toFixed(2))
                                } else {
                                  handleFormChange("bonusesIncentivesPercent", "0")
                                }
                              }}
                              className="pl-8"
                              placeholder="e.g. 1000"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="bonusesIncentivesPercent" className="flex items-center">
                            Bonuses/Incentives (%/year)
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Include bonuses, incentives, etc. Syncs with dollar amount field.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </Label>
                          <div className="relative">
                            <PercentIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="bonusesIncentivesPercent"
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.bonusesIncentivesPercent}
                              onChange={(e) => {
                                let percentValue = e.target.value
                                const parsed = Number.parseFloat(percentValue)
                                
                                // Guard against non-numeric input
                                if (percentValue && !Number.isFinite(parsed)) {
                                  handleFormChange("bonusesIncentivesPercent", "0")
                                  handleFormChange("bonusesIncentives", "0")
                                  return
                                }
                                
                                // Prevent negative numbers
                                if (percentValue && parsed < 0) {
                                  percentValue = "0"
                                }
                                
                                // Limit to 2 decimal places
                                if (percentValue && percentValue.includes('.')) {
                                  const parts = percentValue.split('.')
                                  if (parts[1] && parts[1].length > 2) {
                                    percentValue = `${parts[0]}.${parts[1].substring(0, 2)}`
                                  }
                                }
                                
                                handleFormChange("bonusesIncentivesPercent", percentValue)
                                // Auto-calculate dollar amount from percentage
                                const salary = Number.parseFloat(form.salary) || 0
                                if (percentValue) {
                                  const reparsed = Number.parseFloat(percentValue)
                                  if (Number.isFinite(reparsed)) {
                                    const dollarAmount = salary * (reparsed / 100)
                                    handleFormChange("bonusesIncentives", dollarAmount.toFixed(2))
                                  } else {
                                    handleFormChange("bonusesIncentives", "0")
                                  }
                                } else {
                                  handleFormChange("bonusesIncentives", "0")
                                }
                              }}
                              className="pl-8"
                              placeholder="e.g. 5.00"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="trainingOverhead" className="flex items-center">
                          Training/Overhead ($/year)
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Include training costs, equipment, workspace, etc.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                        <div className="relative">
                          <DollarSignIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="trainingOverhead"
                            type="number"
                            value={form.trainingOverhead}
                            onChange={(e) => handleFormChange("trainingOverhead", e.target.value)}
                            className="pl-8"
                            placeholder="e.g. 2000"
                          />
                        </div>
                      </div>

                      {getSelectedProvince().hasEHT && (
                        <div className="space-y-4">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="includeEHT"
                              checked={form.includeEHT}
                              onCheckedChange={(checked) => handleFormChange("includeEHT", Boolean(checked))}
                            />
                            <Label htmlFor="includeEHT" className="flex items-center">
                              Include Employer Health Tax (EHT)
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>
                                      Applies in {getSelectedProvince().name} for payrolls over{" "}
                                      {formatCurrency(getSelectedProvince().ehtThreshold)}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </Label>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {getSelectedProvince().name} EHT Rate: {getSelectedProvince().ehtRate}%
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <CostBreakdownTable
                  costs={costs}
                  form={form}
                  burdenRate={burdenRate}
                  formatCurrency={formatCurrency}
                  formatPercent={formatPercent}
                />

                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Reporting Pay Rates</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Standard Hourly Rate</p>
                          <p className="text-2xl font-bold">{formatCurrency(costs.standardHourlyRate)}</p>
                          <p className="text-xs text-muted-foreground">(Annual Salary ÷ 2080 hours)</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Reporting Pay Rate</p>
                          <p className="text-2xl font-bold">{formatCurrency(costs.effectiveHourlyRate)}</p>
                          <p className="text-xs text-muted-foreground">
                            (Total Cost ÷ {costs.productiveHours.toFixed(0)} productive hours)
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Based on {form.hoursPerDay} hours/day
                          </p>
                        </div>
                      </div>
                      <div className="text-center pt-2">
                        <p className="text-sm">
                          Total Paid Time-Off: {costs.totalPaidTimeOffDays} days (
                          {((costs.totalPaidTimeOffDays / 260) * 100).toFixed(1)}% of work year)
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="reference" className="space-y-6">
          {/* Real Tax Data Table - show when API data is available */}
          {taxData && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-medium mb-4">Current Tax Rates (API Data)</h3>
                <div className="text-sm text-muted-foreground mb-4">
                  Effective Date: {new Date(taxData.EffectiveDate).toLocaleDateString()}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Employee Rate (%)</TableHead>
                      <TableHead>Max Annual Earnings</TableHead>
                      <TableHead>Max Annual Contribution</TableHead>
                      <TableHead>Annual Exemption</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taxData.TaxTypeCanadaPensionPlan?.IsEligible && (
                      <TableRow>
                        <TableCell>CPP1</TableCell>
                        <TableCell>{taxData.TaxTypeCanadaPensionPlan.EmployeeRatePercentage}%</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeCanadaPensionPlan.MaxPensionableEarnings)}</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeCanadaPensionPlan.MaxDeductions)}</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeCanadaPensionPlan.AnnualExemption)}</TableCell>
                      </TableRow>
                    )}
                    {taxData.TaxTypeCanadaPensionPlan2?.IsEligible && (
                      <TableRow>
                        <TableCell>CPP2 (Enhanced)</TableCell>
                        <TableCell>{taxData.TaxTypeCanadaPensionPlan2.EmployeeRatePercentage}%</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeCanadaPensionPlan2.MaxPensionableEarnings)}</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeCanadaPensionPlan2.MaxDeductions)}</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeCanadaPensionPlan2.AnnualExemption)}</TableCell>
                      </TableRow>
                    )}
                    {taxData.TaxTypeQuebecPensionPlan?.IsEligible && (
                      <TableRow>
                        <TableCell>QPP1</TableCell>
                        <TableCell>{taxData.TaxTypeQuebecPensionPlan.EmployeeRatePercentage}%</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeQuebecPensionPlan.MaxPensionableEarnings)}</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeQuebecPensionPlan.MaxDeductions)}</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeQuebecPensionPlan.AnnualExemption)}</TableCell>
                      </TableRow>
                    )}
                    {taxData.TaxTypeQuebecPensionPlan2?.IsEligible && (
                      <TableRow>
                        <TableCell>QPP2 (Enhanced)</TableCell>
                        <TableCell>{taxData.TaxTypeQuebecPensionPlan2.EmployeeRatePercentage}%</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeQuebecPensionPlan2.MaxPensionableEarnings)}</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeQuebecPensionPlan2.MaxDeductions)}</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeQuebecPensionPlan2.AnnualExemption)}</TableCell>
                      </TableRow>
                    )}
                    {taxData.TaxTypeEmploymentInsurance?.IsEligible && (
                      <TableRow>
                        <TableCell>EI</TableCell>
                        <TableCell>{taxData.TaxTypeEmploymentInsurance.EmployeeRatePercentage}%</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeEmploymentInsurance.MaxInsurable)}</TableCell>
                        <TableCell>{formatCurrency(taxData.TaxTypeEmploymentInsurance.MaxEarnings)}</TableCell>
                        <TableCell>N/A</TableCell>
                      </TableRow>
                    )}
                    {taxData.TaxTypeHealthCare?.IsEligible && (
                      <TableRow>
                        <TableCell>EHT (Employer Health Tax)</TableCell>
                        <TableCell>{taxData.TaxTypeHealthCare.EmployeeRatePercentage}%</TableCell>
                        <TableCell>No Cap</TableCell>
                        <TableCell>No Cap</TableCell>
                        <TableCell>N/A</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Only show the old reference table when API data is not available */}
          {!taxData && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-medium mb-4">CPP/QPP and EI Rates (Reference)</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Rate (%)</TableHead>
                      <TableHead>Annual Max Contribution</TableHead>
                      <TableHead>Max Annual Earnings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>CPP</TableCell>
                      <TableCell>{CPP_QPP_EI_RATES.CPP.rate}%</TableCell>
                      <TableCell>{formatCurrency(CPP_QPP_EI_RATES.CPP.maxContribution)}</TableCell>
                      <TableCell>{formatCurrency(CPP_QPP_EI_RATES.CPP.maxEarnings)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>QPP</TableCell>
                      <TableCell>{CPP_QPP_EI_RATES.QPP.rate}%</TableCell>
                      <TableCell>{formatCurrency(CPP_QPP_EI_RATES.QPP.maxContribution)}</TableCell>
                      <TableCell>{formatCurrency(CPP_QPP_EI_RATES.QPP.maxEarnings)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>EI</TableCell>
                      <TableCell>{CPP_QPP_EI_RATES.EI.rate}%</TableCell>
                      <TableCell>{formatCurrency(CPP_QPP_EI_RATES.EI.maxContribution)}</TableCell>
                      <TableCell>{formatCurrency(CPP_QPP_EI_RATES.EI.maxEarnings)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-medium mb-4">WSIB/WCB Worker Compensation Codes</h3>
              {wcbLoading ? (
                <div className="text-center py-4">Loading worker compensation data...</div>
              ) : wcbError ? (
                <div className="text-center py-4">
                  <div className="text-red-600 mb-2">{wcbError}</div>
                  <div className="text-sm text-muted-foreground">
                    Make sure the API URL is configured correctly and the service is running.
                  </div>
                </div>
              ) : wcbData.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No worker compensation data available
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Subdivision ID</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wcbData.map((item) => (
                      <TableRow key={`${item.Id}-${item.Code}`}>
                        <TableCell>{item.Code}</TableCell>
                        <TableCell>{item.Name}</TableCell>
                        <TableCell>{item.SubdivisionId}</TableCell>
                        <TableCell>{item.StatusId === 1 ? 'Active' : 'Inactive'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-medium mb-4">Employer Health Tax (EHT)</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Province</TableHead>
                    <TableHead>Payroll Threshold</TableHead>
                    <TableHead>Rate (%)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {provinces.filter((p: CalculatorProvince) => p.hasEHT).map((province: CalculatorProvince) => (
                    <TableRow key={province.id}>
                      <TableCell>{province.name}</TableCell>
                      <TableCell>{formatCurrency(province.ehtThreshold)}</TableCell>
                      <TableCell>{province.ehtRate}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>


        </TabsContent>
      </Tabs>

      <div className="flex justify-between items-center pt-6 border-t">
        <div className="text-sm text-muted-foreground">
          <p>Last updated: {new Date().toLocaleDateString()}</p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={handleSave}>Save Calculation</Button>
        </div>
      </div>
    </div>
  )
}
